import React, { useState, useCallback, useRef } from 'react';

import { map, find } from 'lodash';
import PropTypes from 'prop-types';

import { WorkflowAssets, http } from '@services/http';
import { Attachment } from '@components';

const AssetsAttachments = ({
  data,
  title,
  typeText,
  multiple,
  fileType,
  subtitle,
  workflowId,
  acceptFiles,
  handleUpdateData,
  handleDeleteFile,
  noFileDescription,
  noDivider,
  deleteDialogTitle,
  deleteDialogContent,
}) => {
  const downloadedFilesCache = useRef({});

  const [isUploading, setIsUploading] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleUpload = useCallback(() => {
    const formData = new FormData();
    if (!multiple) {
      formData.append(fileType, selectedFiles[0].file);
      formData.append(`${fileType}[caption]`, selectedFiles[0].name);
    } else {
      map(selectedFiles, (data, index) => {
        formData.append(`${fileType}[${index}]`, data.file);
        formData.append(`${fileType}[${index}][caption]`, data.name);
      });
    }
    setIsUploading(true);
    setSelectedFiles([]);
    setOpenUploadDialog(false);
    WorkflowAssets.create({
      workflowId,
      values: formData,
    })
      .then((res) => {
        handleUpdateData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsUploading(false);
      });
  }, [fileType, handleUpdateData, multiple, selectedFiles, workflowId]);

  const handleDelete = useCallback(
    (attachmentId) => {
      WorkflowAssets.delete({
        workflowId,
        assetId: attachmentId,
      })
        .then(() => {
          handleDeleteFile(attachmentId);
        })
        .catch((err) => {
          console.error(err);
        });
    },
    [handleDeleteFile, workflowId],
  );

  const handleDownload = useCallback(
    async (name, attachmentId) => {
      const file = find(data, ['id', attachmentId]);
      try {
        let asset = downloadedFilesCache.current[file.name];

        if (!asset) {
          const res = await http.get(file.download, {
            responseType: 'blob',
          });
          asset = URL.createObjectURL(res.data);
          downloadedFilesCache.current[file.name] = asset;
        }

        const link = document.createElement('a');
        link.href = asset;
        link.setAttribute('download', file.name);
        document.body.appendChild(link);
        link.click();
      } catch (err) {
        console.error(err);
      }
    },
    [data],
  );

  return (
    <Attachment
      noDivider={noDivider}
      data={data}
      title={title}
      typeText={typeText}
      multiple={multiple}
      subtitle={subtitle}
      acceptFiles={acceptFiles}
      isUploading={isUploading}
      handleDelete={handleDelete}
      handleUpload={handleUpload}
      selectedFiles={selectedFiles}
      handleDownload={handleDownload}
      openUploadDialog={openUploadDialog}
      setSelectedFiles={setSelectedFiles}
      noFileDescription={noFileDescription}
      setOpenUploadDialog={setOpenUploadDialog}
      deleteDialogTitle={deleteDialogTitle}
      deleteDialogContent={deleteDialogContent}
    />
  );
};

AssetsAttachments.defaultProps = {
  typeText: 'XML',
};

AssetsAttachments.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  multiple: PropTypes.bool,
  subtitle: PropTypes.string,
  typeText: PropTypes.string,
  fileType: PropTypes.string,
  workflowId: PropTypes.string,
  handleDeleteFile: PropTypes.func,
  handleUpdateData: PropTypes.func,
  noFileDescription: PropTypes.string,
  acceptFiles: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
};

export default AssetsAttachments;
